export const MERGE_DISTANCE_METERS = 12.192; // 40 feet

const R = 6371008.8;
const rad = (v) => (Number(v) * Math.PI) / 180;

export const distanceMeters = (a, b) => {
  const lat1 = rad(a.lat); const lat2 = rad(b.lat);
  const dLat = lat2 - lat1; const dLng = rad(b.lng) - rad(a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

// Nearest point on a short geographic line segment (local metre projection).
export const nearestPointOnSegment = (point, start, end) => {
  const refLat = rad((Number(start.lat) + Number(end.lat) + Number(point.lat)) / 3);
  const toXY = (p) => ({ x: R * rad(p.lng) * Math.cos(refLat), y: R * rad(p.lat) });
  const p = toXY(point); const a = toXY(start); const b = toXY(end);
  const dx = b.x - a.x; const dy = b.y - a.y;
  const denom = dx * dx + dy * dy;
  const t = denom ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / denom)) : 0;
  const nearest = {
    lat: Number(start.lat) + t * (Number(end.lat) - Number(start.lat)),
    lng: Number(start.lng) + t * (Number(end.lng) - Number(start.lng)),
  };
  return { point: nearest, distance: distanceMeters(point, nearest) };
};

export const nearestBorderPoint = (point, zones = []) => {
  let best = null;
  zones.forEach((zone) => {
    const path = zone.path || [];
    for (let i = 0; i < path.length - 1; i += 1) {
      const candidate = nearestPointOnSegment(point, path[i], path[i + 1]);
      if (!best || candidate.distance < best.distance) best = { ...candidate, zone, segmentIndex: i };
    }
  });
  return best;
};

export const getMergeSuggestion = (path = [], zones = [], threshold = MERGE_DISTANCE_METERS) => {
  const matches = path.map((point, index) => ({ index, match: nearestBorderPoint(point, zones) }))
    .filter(({ match }) => match && match.distance > 0.05 && match.distance <= threshold);
  if (!matches.length) return null;
  const counts = matches.reduce((acc, item) => {
    const id = String(item.match.zone.branchId);
    acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});
  const branchId = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  const selected = matches.filter((item) => String(item.match.zone.branchId) === branchId);
  return {
    branchId,
    branchName: selected[0].match.zone.branchName || `Branch #${branchId}`,
    points: selected,
    closestMeters: Math.min(...selected.map((item) => item.match.distance)),
  };
};

export const getMergeCandidates = (path, suggestion) => {
  const selected = [...suggestion.points].sort((a, b) => a.index - b.index);
  if (!selected.length) return [path];
  if (selected.length === 1) {
    return [path.map((p, index) => index === selected[0].index ? selected[0].match.point : p)];
  }

  const first = selected[0]; const last = selected[selected.length - 1];
  let border = [...(first.match.zone.path || [])];
  if (border.length > 1 && distanceMeters(border[0], border[border.length - 1]) < 0.05) border = border.slice(0, -1);
  const n = border.length;
  const forward = [];
  for (let i = (first.match.segmentIndex + 1) % n; i !== (last.match.segmentIndex + 1) % n; i = (i + 1) % n) {
    forward.push(border[i]);
    if (forward.length > n) break;
  }
  const backward = [];
  for (let i = first.match.segmentIndex; i !== last.match.segmentIndex; i = (i - 1 + n) % n) {
    backward.push(border[i]);
    if (backward.length > n) break;
  }
  const makeCandidate = (arc) => [
      ...path.slice(0, first.index), first.match.point, ...arc,
      last.match.point, ...path.slice(last.index + 1),
    ];
  return [makeCandidate(forward), makeCandidate(backward)];
};

export const applyMergeSuggestion = (path, suggestion) => getMergeCandidates(path, suggestion)[0];

const cross = (a, b, c) => (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
const properIntersection = (a, b, c, d) => {
  const abC = cross(a, b, c); const abD = cross(a, b, d);
  const cdA = cross(c, d, a); const cdB = cross(c, d, b);
  // Strict crossing only: a shared/snapped border is valid and is not overlap.
  return abC * abD < -1e-18 && cdA * cdB < -1e-18;
};
const intersectionPoint = (a, b, c, d) => {
  const x1 = a.lng; const y1 = a.lat; const x2 = b.lng; const y2 = b.lat;
  const x3 = c.lng; const y3 = c.lat; const x4 = d.lng; const y4 = d.lat;
  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denominator) < 1e-15) return null;
  return {
    lng: ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / denominator,
    lat: ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / denominator,
  };
};
const pointInPolygon = (point, polygon) => {
  for (let i = 0; i < polygon.length; i += 1) {
    if (nearestPointOnSegment(point, polygon[i], polygon[(i + 1) % polygon.length]).distance < 0.15) return false;
  }
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]; const b = polygon[j];
    if ((a.lat > point.lat) !== (b.lat > point.lat)
      && point.lng < ((b.lng - a.lng) * (point.lat - a.lat)) / ((b.lat - a.lat) || 1e-15) + a.lng) inside = !inside;
  }
  return inside;
};
export const findOverlap = (path, zones = []) => zones.find((zone) => {
  const other = zone.path || [];
  for (let i = 0; i < path.length; i += 1) {
    for (let j = 0; j < other.length; j += 1) {
      if (properIntersection(path[i], path[(i + 1) % path.length], other[j], other[(j + 1) % other.length])) return true;
    }
  }
  return pointInPolygon(path[0], other) || pointInPolygon(other[0], path);
}) || null;

export const getOverlapDetails = (path, zones = []) => zones.map((zone) => {
  const other = zone.path || [];
  const points = [];
  for (let i = 0; i < path.length; i += 1) {
    for (let j = 0; j < other.length; j += 1) {
      const a = path[i]; const b = path[(i + 1) % path.length];
      const c = other[j]; const d = other[(j + 1) % other.length];
      if (properIntersection(a, b, c, d)) {
        const point = intersectionPoint(a, b, c, d);
        if (point) points.push(point);
      }
    }
  }
  path.forEach((point) => { if (pointInPolygon(point, other)) points.push(point); });
  other.forEach((point) => { if (pointInPolygon(point, path)) points.push(point); });
  return { zone, points };
}).filter((detail) => detail.points.length > 0);

export const getOverlapScore = (path, zones = []) => zones.reduce((total, zone) => {
  const other = zone.path || [];
  let score = 0;
  for (let i = 0; i < path.length; i += 1) {
    for (let j = 0; j < other.length; j += 1) {
      if (properIntersection(path[i], path[(i + 1) % path.length], other[j], other[(j + 1) % other.length])) score += 1;
    }
  }
  path.forEach((point) => { if (pointInPolygon(point, other)) score += 2; });
  other.forEach((point) => { if (pointInPolygon(point, path)) score += 2; });
  return total + score;
}, 0);
